<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import { adminApi } from '@/api/admin'
import { ApiError } from '@/api/errors'
import { useReauth } from '@/composables/useReauth'

// Shown when a sensitive action returns 423 (SPEC-002 §7). After a
// successful confirmation the API client retries the original request.
const { open, finish } = useReauth()
const password = ref('')
const code = ref('')
const error = ref('')
const busy = ref(false)

watch(open, (isOpen) => {
  if (isOpen) {
    password.value = ''
    code.value = ''
    error.value = ''
  }
})

async function confirm(): Promise<void> {
  busy.value = true
  error.value = ''
  try {
    await adminApi.confirmIdentity(password.value, code.value)
    finish(true)
  } catch (e) {
    error.value = ApiError.from(e).message
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <BaseModal :show="open" :title="tr('Confirm it\'s you')" elevated @close="finish(false)">
    <form class="space-y-4" @submit.prevent="confirm">
      <p class="text-sm text-default">
        {{ tr('This change needs your password and a fresh code from Google Authenticator. You won\'t be asked again for 15 minutes.') }}
      </p>
      <BaseInput
        v-model="password"
        :label="tr('Password')"
        type="password"
        autocomplete="current-password"
        required
      />
      <BaseInput
        v-model="code"
        :label="tr('Authenticator code')"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        pattern="[0-9]{6}"
        required
      />
      <p v-if="error" class="text-sm text-red-600" role="alert">{{ tr(error) }}</p>
      <div class="flex justify-end gap-2">
        <BaseButton variant="ghost" @click="finish(false)">{{ tr('Cancel') }}</BaseButton>
        <BaseButton type="submit" :loading="busy">{{ tr('Confirm') }}</BaseButton>
      </div>
    </form>
  </BaseModal>
</template>
